/**
 * Semantic date format templates for use with dayjs format() function.
 *
 *
 * @example
 * ```ts
 * import { format, day, DATE_TEMPLATES } from 'astra'
 *
 * const isoDate = format(day(), DATE_TEMPLATES.iso)
 * const norwegianDate = format(day(), DATE_TEMPLATES.norwegianDate)
 * ```
 */
export const DATE_TEMPLATES = {
    /**
     * ISO 8601 date format: YYYY-MM-DD
     * @example "2024-12-04"
     */
    iso: 'YYYY-MM-DD',

    /**
     * ISO 8601 datetime format: YYYY-MM-DD HH:mm
     * @example "2024-12-04 14:30"
     */
    isoDateTime: 'YYYY-MM-DD HH:mm',

    /**
     * Norwegian date format: D. MMM YYYY
     * @example "4. des. 2024"
     */
    norwegianDate: 'D. MMM YYYY',

    /**
     * Norwegian datetime format with time: D. MMM YYYY kl. HH:mm
     * @example "4. des. 2024 kl. 14:30"
     */
    norwegianDateTime: 'D. MMM YYYY kl. HH:mm',

    /**
     * 24-hour time format: HH:mm
     * @example "14:30"
     */
    time: 'HH:mm',

    /**
     * Compact date with abbreviated month and time: D. MMM HH:mm
     * @example "4. des. 14:30"
     */
    compactDateTime: 'D. MMM HH:mm',

    /**
     * Long month format: D. MMMM YYYY
     * @example "4. desember 2024"
     */
    longMonthFullYear: 'D. MMMM YYYY',

    /**
     * Long month format: D. MMMM
     * @example "4. desember"
     */
    longMonth: 'D. MMMM',

    /**
     * Very short day/month format: D/M
     * @example "4/12"
     */
    dayMonth: 'D/M',

    /**
     * Full weekday name and date: dddd D. MMM YYYY
     * @example "mandag 4. des. 2024"
     */
    fullWeekdayNorwegianDate: 'dddd D. MMM YYYY',

    /**
     * Full weekday name and month: dddd D. MMMM
     * @example "mandag 4. desember"
     */
    fullWeekDayMonth: 'dddd D. MMMM',

    /**
     * Short weekday name and date: ddd. D. MMM
     * @example "man. 4. des."
     */
    shortWeekDayMonth: 'ddd. D. MMM',
    /**
     * Month and year with full month name: MMM YYYY
     * @example "des. 2024"
     */
    monthYear: 'MMM YYYY',

    /**
     * Full month and year: MMMM YYYY
     * @example "desember 2024"
     */
    monthYearFull: 'MMMM YYYY',

    /**
     * Short month and year: MMM YY
     * @example "des. 24"
     */
    monthYearShort: 'MMM YY',

    /**
     * Full weekday name: dddd
     * @example "mandag"
     */
    fullWeekday: 'dddd',

    /**
     * Abbreviated month format: D. MMM
     * @example "4. des."
     */
    dateShortMonth: 'D. MMM',
} as const

export type DateFormatKey = keyof typeof DATE_TEMPLATES
export type DateFormatValue = (typeof DATE_TEMPLATES)[DateFormatKey]
